export const INLINE_FORMATS = Object.freeze({
  bold: { prefix: "**", suffix: "**", placeholder: "bold text" },
  italic: { prefix: "*", suffix: "*", placeholder: "italic text" },
  underline: { prefix: "++", suffix: "++", placeholder: "underlined text" },
  strikethrough: { prefix: "~~", suffix: "~~", placeholder: "struck text" },
  code: { prefix: "`", suffix: "`", placeholder: "inline code" },
});

const normalizedRange = (value, selectionStart, selectionEnd) => {
  const length = value.length;
  const start = Math.max(0, Math.min(Number(selectionStart) || 0, length));
  const end = Math.max(start, Math.min(Number(selectionEnd) || start, length));
  return { start, end };
};

const repeatedAtStart = (value, character) => {
  let count = 0;
  while (value[count] === character) count += 1;
  return count;
};

const repeatedAtEnd = (value, character) => {
  let count = 0;
  while (value[value.length - count - 1] === character) count += 1;
  return count;
};

const repeatedBefore = (value, position, character) => {
  let count = 0;
  while (position - count - 1 >= 0 && value[position - count - 1] === character) count += 1;
  return count;
};

const repeatedAfter = (value, position, character) => {
  let count = 0;
  while (position + count < value.length && value[position + count] === character) count += 1;
  return count;
};

const activeAsteriskCount = (formatName, beforeCount, afterCount) => {
  if (formatName === "bold") return beforeCount >= 2 && afterCount >= 2 ? 2 : 0;
  if (formatName === "italic") return beforeCount % 2 === 1 && afterCount % 2 === 1 ? 1 : 0;
  return 0;
};

/** Toggle an inline Markdown marker around the current editor selection. */
export const formatInlineSelection = (
  inputValue,
  selectionStart,
  selectionEnd,
  formatName
) => {
  const value = String(inputValue || "");
  const format = INLINE_FORMATS[formatName];
  if (!format) return null;

  const { start, end } = normalizedRange(value, selectionStart, selectionEnd);
  const { prefix, suffix, placeholder } = format;
  const selectedText = value.slice(start, end);

  if (selectedText.includes("\n")) {
    const formattedText = selectedText
      .split("\n")
      .map((line) => {
        if (!line) return line;
        return formatInlineSelection(line, 0, line.length, formatName).value;
      })
      .join("\n");
    return {
      value: `${value.slice(0, start)}${formattedText}${value.slice(end)}`,
      selectionStart: start,
      selectionEnd: start + formattedText.length,
    };
  }

  if (prefix === "*" || prefix === "**") {
    const markerCount = selectedText
      ? activeAsteriskCount(
          formatName,
          repeatedAtStart(selectedText, "*"),
          repeatedAtEnd(selectedText, "*")
        )
      : 0;
    if (markerCount) {
      const unwrapped = selectedText.slice(markerCount, selectedText.length - markerCount);
      return {
        value: `${value.slice(0, start)}${unwrapped}${value.slice(end)}`,
        selectionStart: start,
        selectionEnd: start + unwrapped.length,
      };
    }

    const surroundingMarkerCount = selectedText
      ? activeAsteriskCount(
          formatName,
          repeatedBefore(value, start, "*"),
          repeatedAfter(value, end, "*")
        )
      : 0;
    if (surroundingMarkerCount) {
      const unwrappedStart = start - surroundingMarkerCount;
      return {
        value: `${value.slice(0, unwrappedStart)}${selectedText}${value.slice(end + surroundingMarkerCount)}`,
        selectionStart: unwrappedStart,
        selectionEnd: unwrappedStart + selectedText.length,
      };
    }
  }

  if (
    prefix !== "*" &&
    prefix !== "**" &&
    selectedText &&
    selectedText.startsWith(prefix) &&
    selectedText.endsWith(suffix) &&
    selectedText.length >= prefix.length + suffix.length
  ) {
    const unwrapped = selectedText.slice(prefix.length, selectedText.length - suffix.length);
    return {
      value: `${value.slice(0, start)}${unwrapped}${value.slice(end)}`,
      selectionStart: start,
      selectionEnd: start + unwrapped.length,
    };
  }

  const wrappedBySelection =
    prefix !== "*" &&
    prefix !== "**" &&
    start >= prefix.length &&
    value.slice(start - prefix.length, start) === prefix &&
    value.slice(end, end + suffix.length) === suffix;

  if (selectedText && wrappedBySelection) {
    const unwrappedStart = start - prefix.length;
    return {
      value: `${value.slice(0, unwrappedStart)}${selectedText}${value.slice(end + suffix.length)}`,
      selectionStart: unwrappedStart,
      selectionEnd: unwrappedStart + selectedText.length,
    };
  }

  const content = selectedText || placeholder;
  return {
    value: `${value.slice(0, start)}${prefix}${content}${suffix}${value.slice(end)}`,
    selectionStart: start + prefix.length,
    selectionEnd: start + prefix.length + content.length,
  };
};

export const isSafeInlineLink = (value) =>
  /^(https?:\/\/|mailto:)[^\s<>()]+$/i.test(String(value || "").trim());

/** Create a safe Markdown link without ever injecting raw HTML. */
export const linkInlineSelection = (
  inputValue,
  selectionStart,
  selectionEnd,
  linkUrl
) => {
  const value = String(inputValue || "");
  const url = String(linkUrl || "").trim();
  if (!isSafeInlineLink(url)) return null;

  const { start, end } = normalizedRange(value, selectionStart, selectionEnd);
  const selectedText = value.slice(start, end);
  if (selectedText.includes("\n")) return null;
  const label = (selectedText || "link text").replace(/\[|\]/g, "");
  const replacement = `[${label || "link text"}](${url})`;

  return {
    value: `${value.slice(0, start)}${replacement}${value.slice(end)}`,
    selectionStart: start + 1,
    selectionEnd: start + 1 + (label || "link text").length,
  };
};
